import Fields from 'Components/brands/Fields'
import Loader from 'Components/Common/Loader';
import { PRIVILEGE } from 'constants/privilege';
import { useFormik } from 'formik';
import withAccessControl from 'hoc/withAccessControl';
import useDocumentTitle from 'hooks/useDocumentTitle';
import Error404 from 'pages/Error/Error404';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card, CardBody, Form, Spinner } from 'reactstrap'
import { updateBrand } from 'services/api/brands';
import { fetchBrandList } from 'slices/brand/list/thunk';
import { fetchBrandData } from 'slices/brand/thunk';
import { AppDispatch, RootState } from 'store';
import { NewBrand } from 'types/brand';
import { getErrorMessage } from 'utils/helpers';
import * as Yup from "yup";

const EditBrand = () => {

    useDocumentTitle("Edit Brand");

    const { brandId } = useParams();
    const dispatch: AppDispatch = useDispatch();
    const [brandLogo, setBrandLogo] = useState<File | null>(null);

    const selectedAgency = useSelector((state: RootState) => state.auth.selectedAgency);

    const { brand } = useSelector((state: RootState) => state.brandData.brands[brandId!] ?? { brand: null });

    const loading = useSelector((state: RootState) => state.brandData.loading);

    const [updating, setUpdating] = useState(false);

    const formFields = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: {
            name: brand?.name ?? '',
            description: brand?.description ?? '',
            website_url: brand?.website_url ?? '',
            ig_handle: brand?.ig_handle ?? '',
            img_url: brand?.img_url ?? null
        },
        validationSchema: Yup.object({
            name: Yup.string().required("Brand name field can't be empty").max(50, "Maximum 50 characters are allowed"),
            description: Yup.string().required("Brand description field can't be empty"),
            website_url: Yup.string().nullable().required("Brand website field can't be empty"),
        }),
        onSubmit: async (brand) => {
            try {
                if (brandLogo && brandLogo.size > 2 * 1024 * 1024) {
                    formFields.setFieldError('img_url', "Image size must not exceed 2MB")
                    return;
                }

                setUpdating(true);
                const updatedBrand: NewBrand = { ...brand, ...(brandLogo ? { img: brandLogo } : {}) };

                await updateBrand(selectedAgency?.uuid!, brandId!, updatedBrand);
                toast.success("Brand updated successfully!");
                dispatch(fetchBrandData(Number(brandId)));
                dispatch(fetchBrandList({ page: 1, force: true }));
            } catch (error) {
                toast.error(getErrorMessage(error, 'Failed to create new brand'));
            } finally {
                setUpdating(false)
            }
        }
    });


    useEffect(() => {
        if (brandId)
            dispatch(fetchBrandData(Number(brandId)));
    }, [brandId, dispatch])

    if (loading) return <Loader />

    if (!loading && !brand) return <Error404 />

    return (
        <div className="page-content">
            <div className="page-content-inner page-spacing">
                <div className="d-flex w-100 justify-content-between align-items-center pt-1 pb-5">
                    <h4 className="page-title">Edit Brand</h4>
                </div>
                <Form className='needs-validation' onSubmit={(e) => {
                    e.preventDefault();
                    formFields.handleSubmit();
                    return false;
                }}>
                    <div className="brand-create-card border-0 position-relative mt-2">
                        <div className="brand-create-card__body">
                            <Fields formFields={formFields} onBrandLogoChange={(logo) => setBrandLogo(logo)} />
                        </div>
                    </div>
                    <div className="d-flex justify-content-between my-4">
                        <Link to={{ pathname: '/brand' }} type='button' className="theme-btn theme-btn-lg btn-primary-outlined"><i className='bx fs-4 bx-left-arrow-alt me-2' ></i>Back</Link>
                        <button type='submit' disabled={updating} className="theme-btn theme-btn-lg btn-gradient form-continue-btn">
                            {
                                loading ? <Spinner size="sm" className='me-2'> Loading... </Spinner> : <React.Fragment>Save <i className='bx fs-4 bx-right-arrow-alt' ></i></React.Fragment>
                            }
                        </button>
                    </div>
                </Form>
            </div>

        </div>
    )
}

export default withAccessControl(EditBrand, PRIVILEGE.BRAND.BRAND.EDIT)